import { useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';
import { ReviewQueueCard } from '@/components/orcamento/ReviewQueueCard';
import { reviewItemKey } from '@/components/orcamento/helpers';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { revalidateReviewQueues } from '@/hooks/queries/swrConfig';
import { useReviewQueue, revalidatePostRefundCaches } from '@/hooks/queries/useReviewQueue';
import { investmentsApi } from '@/lib/api';
import { notifyError } from '@/lib/notify-error';
import { track, type DuplicateDecision } from '@/lib/telemetry';
import type { ReviewQueueItem } from '@/types';

interface Props {
  /** Called once a decision lands, so the page reloads positions and totals. */
  onResolved: () => void;
}

export function InvestmentDuplicateReviewCard({ onResolved }: Props) {
  const { data: queue, mutate } = useReviewQueue('investment');
  const { mutate: mutateGlobal } = useSWRConfig();
  const authedFetch = useAuthedFetcher();
  const [open, setOpen] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const decide = useCallback(
    async (item: ReviewQueueItem, decision: DuplicateDecision) => {
      // One decision at a time: only the busy row is disabled, so without this a click on a
      // second row would re-enable the first and let two conflicting requests run at once.
      if (busyKey) return;
      const suggested = item.duplicateSuggestion?.suggestedRole === 'ADDITIVE' ? 'additive' : 'mirror';
      setBusyKey(reviewItemKey(item));

      try {
        try {
          await authedFetch((token) => investmentsApi.decideDuplicate(token, item.id, decision));
        } catch (err) {
          // Only the decision request itself failing means the decision was not saved.
          // notifyError already surfaces the toast — a second toast.error would double it.
          notifyError('investment_duplicate_review', err, 'Não foi possível salvar sua decisão. Tente novamente.');
          return;
        }

        // Persisted. Tracked only now: a failed attempt is not a review, and counting it would
        // overstate how often the suggestions are accepted.
        track('action.investment.duplicate_reviewed', {
          decision,
          suggested_role: suggested,
          followed_suggestion: decision === suggested,
          ...(item.duplicateSuggestion?.matchReason
            ? { match_reason: item.duplicateSuggestion.matchReason }
            : {}),
        });

        // The refreshes are best-effort: the server already changed the grouping, so a transient
        // revalidation error must not tell the user the decision failed or skip reloading totals.
        try {
          await mutate();
          // The other queue slices and the home widget count the same pendência.
          revalidateReviewQueues(mutateGlobal);
          // A grouping decision moves the portfolio total the home widgets read.
          revalidatePostRefundCaches(mutateGlobal);
        } catch {
          // Swallowed: the next natural revalidation reconciles the caches.
        }
        onResolved();
      } finally {
        setBusyKey(null);
      }
    },
    [authedFetch, busyKey, mutate, mutateGlobal, onResolved],
  );

  const handleConfirm = useCallback(
    (item: ReviewQueueItem, role: 'MIRROR' | 'ADDITIVE') =>
      void decide(item, role === 'MIRROR' ? 'mirror' : 'additive'),
    [decide],
  );

  const handleDismiss = useCallback(
    (item: ReviewQueueItem) => void decide(item, 'not_duplicate'),
    [decide],
  );

  // Self-guarding, like RefundReviewCard: an empty queue leaves no trace on the page.
  if (!queue || queue.total === 0 || queue.results.length === 0) return null;

  return (
    <ReviewQueueCard
      items={queue.results}
      total={queue.total}
      subtitle="posições que aparecem em mais de uma conexão — confirme se são a mesma aplicação ou aplicações diferentes"
      open={open}
      onOpenChange={setOpen}
      confirmingId={busyKey}
      confirmingAll={false}
      onConfirmDuplicate={handleConfirm}
      onDismissDuplicate={handleDismiss}
    />
  );
}
